import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";

// Hooks
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import { useFactoryWashout } from "@/Washouts/hooks/useFactoryWashout";

// API
import { getWashoutsSidePanelTree } from "@/Washouts/api/common/queries";

// Helpers
import { expandActiveCommissionCycle, refreshSidePanel } from "@/Washouts/helpers/sidePanelTrees";
import { addWashoutSubscriptions } from "@/Washouts/helpers/washoutSubscriptions";

// Components
import BodyLayer from "@/Layouts/components/Layers/BodyLayer";
import CollapsibleLeftPanel from "@/Layouts/components/CollapsibleLeftPanel";
import GeneralSalesManagerWashoutContainer from "./GeneralSalesManagerWashoutContainer";
import WashoutsSideBar from "@/Washouts/layouts/WashoutsSideBar";
import useCacheCleaner from "@/Common/hooks/CacheCleaner";

// Constants
const POSITION = "General Sales Manager";
const EMPLOYEE_ORDER_LABEL = "By Employee";
const QUERY_IDENTIFIERS = ["getWashout"];

export default function GeneralSalesManagerWashoutLayer() {
	const { clearCache } = useCacheCleaner();
	const [selected, setSelected] = useState(null);
	const [expanded, setExpanded] = useState([]);
	const [toggleTitle, setToggleTitle] = useState(EMPLOYEE_ORDER_LABEL);
	const [open, setOpen] = useState(true);

	const [activeCommissionCycles, setActiveCommissionCycles] = useState([]);

	const { addSubscription, clearSubscriptionContext } = useSubscriptions();

	// Washout subscriptions
	const { useWashoutSubscriptions } = useFactoryWashout();
	const subscribeWashout = useWashoutSubscriptions(POSITION);
	const onCreateWashout = subscribeWashout?.get("onCreateWashout");
	const onUpdateWashout = subscribeWashout?.get("onUpdateWashout");

	const { location, locationId } = useSelector((state) => state.location);
	const queryClient = useQueryClient();

	const subscriptionsInformation = {
		subscription: {
			context: "GeneralSalesManagersWashoutsCommissionCycles",
			treeContext: "GeneralSalesManagersWashoutsTree",
			hook: addSubscription,
		},
		onNewWashout: {
			context: "onNewSalesDepartmentalWashoutAvailable",
			query: onCreateWashout?.query,
		},
		onUpdateWashout: {
			context: "onUpdateSalesDepartmentalWashout",
			query: onUpdateWashout?.query,
		},
	};

	const { data: washoutsSidePanelTree, isLoading } = useQuery({
		queryKey: ["GeneralSalesManagersWashoutsTree", location?.locationId],
		queryFn: async () => {
			const tree = await retrieveGeneralSalesManagerWashoutsTreeData();
			if (tree) {
				const nodesToExpand = expandActiveCommissionCycle(tree.tree, tree.activeCommissionCycles, toggleTitle);
				setExpanded(nodesToExpand);
				setActiveCommissionCycles(tree.activeCommissionCycles);
				return tree;
			} else {
				return null;
			}
		},

		staleTime: Infinity,
		enabled: !!location?.locationId,
	});

	const retrieveGeneralSalesManagerWashoutsTreeData = async () => {
		const tree = await getWashoutsSidePanelTree(location.locationId, POSITION);
		if (tree) {
			const activeAndClosingCommissionCycles = await addWashoutSubscriptions(POSITION, tree.tree, subscriptionsInformation);
			setActiveCommissionCycles(activeAndClosingCommissionCycles);
			return {
				tree: tree?.tree || [],
				activeCommissionCycles: activeAndClosingCommissionCycles,
			};
		} else {
			return false;
		}
	};

	const handleSelect = (currentSelected) => {
		if (currentSelected?._id && currentSelected._id !== selected?._id) {
			setOpen(!open);
			setSelected(currentSelected);
		}
	};

	const handleOrderBy = (order) => {
		setToggleTitle(order);
		setExpanded([]);
	};

	const updateSidePanelCallBack = async (selectedNode) => {
		const washoutTreeCache = queryClient.getQueryData(["GeneralSalesManagersWashoutsTree", location?.locationId], (data) => data);
		const { tree = [], activeCommissionCycles: cycles } = washoutTreeCache || {};

		refreshSidePanel(
			{
				tree,
				selectedNode,
				activeCommissionCycles: cycles,
			},
			{
				toggleTitle,
				useRanking: false,
			},
			{
				setExpanded,
				setSelected,
			}
		);
	};

	useEffect(() => {
		if (!location) {
			return;
		}

		return () => {
			setSelected(null);
			setToggleTitle(EMPLOYEE_ORDER_LABEL);
			clearSubscriptionContext();
			clearCache(QUERY_IDENTIFIERS);
			setOpen(true);
		};
	}, [location, clearSubscriptionContext]);

	const isTreeLoading = isLoading || locationId !== location?.locationId;

	return (
		<BodyLayer
			floatingContent={
				<CollapsibleLeftPanel
					loading={isTreeLoading}
					open={true}
					permanentTrigger={open}
					control={
						<WashoutsSideBar
							treeDefinition={washoutsSidePanelTree}
							loading={isTreeLoading}
							selected={selected?._id ?? ""}
							toggleTitle={toggleTitle}
							onSelectAction={handleSelect}
							onOrderByAction={handleOrderBy}
						/>
					}
				/>
			}
		>
			<GeneralSalesManagerWashoutContainer
				selected={selected}
				dataPresent={Boolean(selected)}
				location={location}
				updateSidePanelCallBack={updateSidePanelCallBack}
				activeCommissionCycles={activeCommissionCycles}
				subscriptionsInformation={subscriptionsInformation}
			/>
		</BodyLayer>
	);
}
